import React, { useState, useEffect } from "react";
import {
  ClockIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  ClockIcon as ClockIconSolid,
} from "@heroicons/react/24/solid";

const TimeUpModal = ({ isOpen, onAutoSubmit }) => {
  const [countdown, setCountdown] = useState(5);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setCountdown(5);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onAutoSubmit]);

  if (!isOpen && !isVisible) return null;

  const progressPercentage = ((5 - countdown) / 5) * 100;

  return (
    <>
      {/* Backdrop with modern blur effect */}
      <div
        className={`fixed inset-0 bg-gradient-to-br from-red-900/90 via-orange-900/60 to-pink-900/70 backdrop-blur-lg z-[9999] transition-all duration-500 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Modal Container */}
      <div
        className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 transition-all duration-500 ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"
        }`}
      >
        <div className="relative w-full max-w-lg bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden transform transition-all duration-500">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-red-400/30 to-orange-600/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-pink-400/30 to-red-600/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-yellow-400/20 to-orange-600/20 rounded-full blur-2xl animate-pulse delay-500"></div>
          </div>

          {/* Header Section */}
          <div className="relative px-8 pt-12 pb-6 text-center">
            {/* Warning Icon with enhanced animations */}
            <div className="relative inline-flex items-center justify-center mb-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-2xl animate-pulse">
                <ClockIconSolid className="w-12 h-12 text-white animate-bounce" />
              </div>
              {/* Multiple floating rings */}
              <div className="absolute inset-0 rounded-full border-4 border-red-300/40 animate-ping"></div>
              <div className="absolute inset-0 rounded-full border-2 border-orange-400/60 animate-pulse delay-300"></div>
              <div className="absolute inset-0 rounded-full border-3 border-red-400/30 animate-ping delay-700"></div>
            </div>

            <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent mb-3">
              Hết thời gian!
            </h2>
            <p className="text-gray-600 text-lg font-medium">
              Bạn đã hết thời gian làm bài thi
            </p>
          </div>

          {/* Countdown Section */}
          <div className="px-8 pb-6">
            {/* Large Countdown Display */}
            <div className="flex items-center justify-center mb-8">
              <div className="relative">
                {/* Outer rotating ring */}
                <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-red-200"></div>
                <div
                  className="absolute inset-0 w-32 h-32 rounded-full border-4 border-red-500 border-t-transparent animate-spin"
                  style={{ animationDuration: "2s" }}
                ></div>

                {/* Inner countdown circle */}
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-red-50 to-orange-50 border-4 border-white shadow-inner flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold bg-gradient-to-br from-red-600 to-orange-600 bg-clip-text text-transparent">
                      {countdown}
                    </div>
                    <div className="text-xs text-red-500 font-medium mt-1">
                      giây
                    </div>
                  </div>
                </div>

                {/* Pulsing effect */}
                <div className="absolute inset-0 w-32 h-32 rounded-full bg-red-400/20 animate-ping"></div>
              </div>
            </div>

            {/* Status Messages */}
            <div className="space-y-4 mb-6">
              {/* Auto Submit Warning */}
              {/* <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-5">
                <div className="flex items-center mb-3">
                  <div className="bg-red-100 rounded-full p-2 mr-3">
                    <BoltIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-red-800 font-bold text-lg">
                      Tự động nộp bài sau {countdown} giây
                    </p>
                    <p className="text-red-600 text-sm">
                      Hệ thống sẽ tự động xử lý bài thi của bạn
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Reassurance Message */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-4">
                <div className="flex items-center">
                  <div className="bg-blue-100 rounded-full p-2 mr-3">
                    <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-blue-800 font-semibold text-sm">
                      Đáp án đã được lưu tự động
                    </p>
                    <p className="text-blue-600 text-xs">
                      Tất cả câu trả lời của bạn đã được bảo vệ an toàn
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Progress Bar */}
          </div>

          {/* Footer with additional visual elements */}
          {/* <div className="px-8 pb-8">
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-4 border border-gray-100">
              <div className="flex items-center justify-center text-gray-600">
                <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">
                  Vui lòng chờ trong giây lát...
                </span>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </>
  );
};

export default TimeUpModal;
